import { detach, Instance, types } from 'mobx-state-tree'

import { IsEditingModel } from '../IsEditingModel'

import { ChecklistKind, CHECKLIST_KIND_DISPLAY_NAMES } from './ChecklistKind'

const ChecklistInstructionModel = types
  .model('ChecklistInstructionModel', {
    caption: types.string,
  })
  .actions((self) => ({
    setCaption(caption: string) {
      self.caption = caption
    },
  }))

export type ChecklistInstruction = Instance<typeof ChecklistInstructionModel>

const ChecklistBaseModel = IsEditingModel.named('ChecklistBaseModel')
  .props({
    checklist_type: types.enumeration('ChecklistKind', [...Object.values(ChecklistKind)]),
    name: types.string,
    instructions: types.array(ChecklistInstructionModel),
  })
  .views((self) => ({
    get checklistKind(): ChecklistKind {
      return self.checklist_type
    },
  }))
  .views((self) => ({
    get checklistKindDisplayName() {
      return CHECKLIST_KIND_DISPLAY_NAMES[self.checklist_type]
    },
  }))
  .actions((self) => ({
    setName(name: typeof self.name) {
      self.name = name
    },
    setChecklistKind(kind: typeof self.checklist_type) {
      self.checklist_type = kind
    },
    addStep() {
      self.instructions.push(ChecklistInstructionModel.create({ caption: '' }))
    },
    deleteStep(stepIndex: number) {
      self.instructions.splice(stepIndex, 1)
    },
    moveStep(fromIndex: number, toIndex: number) {      
      const step = self.instructions[fromIndex]
      if (!step) {
        throw new Error(`No step at index ${fromIndex}`)
      }

      detach(step) // Removes step from the tree, hence from the array
      self.instructions.splice(toIndex, 0, step) // Put it it the new position, reattaches to the tree.
    }
  }))

export const ExistingChecklistModel = ChecklistBaseModel.named('ExistingChecklistModel').props({
  id: types.identifierNumber,
})
export const NewChecklistModel = ChecklistBaseModel.named('NewChecklistModel')
export const ChecklistModel = types.union(NewChecklistModel, ExistingChecklistModel)

export type Checklist = Instance<typeof ChecklistModel>
export type ExistingChecklist = Instance<typeof ExistingChecklistModel>

// When a checklist object is edited, we edit not the instance in the checklist store, but a temporary clone (in order not to change the object displayed in the table).
// However, in objects with an identifier, and are referenced by this identifier,  we can't have two instances of the same type with the same Id, so we can't create a clone.
// To circumvent this, we define a new type for editing. Now the edit clones are not of the same type, so we don't have this problem.
export const ExistingChecklistEditCloneModel = ExistingChecklistModel.named('ExistingChecklistEditCloneModel')
export const NewChecklistEditCloneModel = NewChecklistModel.named('NewChecklistEditCloneModel').props({
  checklist_type: types.maybeNull(types.enumeration('ChecklistKind', [...Object.values(ChecklistKind)])),
  name: types.optional(types.string, ''),
})
export const ChecklistEditCloneModel = types.union(ExistingChecklistEditCloneModel, NewChecklistEditCloneModel)

export type ChecklistEditClone = Instance<typeof ChecklistEditCloneModel>
